import { MatrixSearchInput } from '@/components/matrix-grid/matrix-search-input';
import { MultiSelectFilter } from '@/components/matrix-grid/multi-select-filter';
import { SortControls } from '@/components/matrix-grid/sort-controls';
import {
  type CreatedByFilter,
  DESTINATAIRE_FILTER_LABELS,
  DESTINATAIRE_FILTER_VALUES,
  MATRIX_DISPLAY_STATES,
  MATRIX_DISPLAY_STATE_LABELS,
  type MatrixGridSettings,
  type MatrixGridSort,
  type MatrixGridView,
} from '@/lib/matrix-grid-search';
import { MATRIX_PIVOTS, MATRIX_PIVOT_LABELS } from '@mss_creator/shared';
import { Button, SegmentedControl } from '@pms-digital/design-system';

export interface MatrixGridToolbarProps {
  settings: MatrixGridSettings;
  onChange: (next: MatrixGridSettings) => void;
  onSortChange: (sort: MatrixGridSort) => void;
  onToggleDirection: () => void;
  /** « Nouveau dossier », at the right end of the bar. */
  onNewFolder: () => void;
}

const STATE_OPTIONS = MATRIX_DISPLAY_STATES.map((value) => ({
  value,
  label: MATRIX_DISPLAY_STATE_LABELS[value],
}));
const PIVOT_OPTIONS = MATRIX_PIVOTS.map((value) => ({ value, label: MATRIX_PIVOT_LABELS[value] }));
const DESTINATAIRE_OPTIONS = DESTINATAIRE_FILTER_VALUES.map((value) => ({
  value,
  label: DESTINATAIRE_FILTER_LABELS[value],
}));

const CREATED_BY_OPTIONS: readonly { value: CreatedByFilter; label: string }[] = [
  { value: 'me', label: 'Moi' },
  { value: 'all', label: 'Tout le monde' },
];

const VIEW_OPTIONS: readonly { value: MatrixGridView; label: string; icon: string }[] = [
  { value: 'grid', label: 'Grille', icon: 'grid_view' },
  { value: 'list', label: 'Liste', icon: 'list' },
];

/**
 * The bar above the matrix grid (issue #16, planche 03): text search, the
 * État / Pivot / Destinataire multi-filters, « Créée par » (Moi / Tout le
 * monde), « Trier par » with its direction, the grid / list switch, and (issue
 * #19) « Nouveau dossier » on the right.
 */
export function MatrixGridToolbar({
  settings,
  onChange,
  onSortChange,
  onToggleDirection,
  onNewFolder,
}: MatrixGridToolbarProps) {
  return (
    <div className="flex flex-wrap items-center gap-1">
      <MatrixSearchInput value={settings.q} onChange={(q) => onChange({ ...settings, q })} />
      <MultiSelectFilter
        label="État"
        options={STATE_OPTIONS}
        selected={settings.states}
        onChange={(states) => onChange({ ...settings, states })}
      />
      <MultiSelectFilter
        label="Pivot"
        options={PIVOT_OPTIONS}
        selected={settings.pivots}
        onChange={(pivots) => onChange({ ...settings, pivots })}
      />
      <MultiSelectFilter
        label="Destinataire"
        options={DESTINATAIRE_OPTIONS}
        selected={settings.destinataires}
        onChange={(destinataires) => onChange({ ...settings, destinataires })}
      />
      <SegmentedControl
        label="Créée par"
        legend="Créée par"
        value={settings.createdBy}
        onChange={(createdBy) => onChange({ ...settings, createdBy: createdBy as CreatedByFilter })}
        options={CREATED_BY_OPTIONS}
      />
      <span className="flex-1" />
      <SortControls
        sort={settings.sort}
        dir={settings.dir}
        onSortChange={onSortChange}
        onToggleDirection={onToggleDirection}
      />
      <span aria-hidden="true" className="mx-1 h-6 w-px bg-gray-100" />
      <SegmentedControl
        label="Affichage"
        value={settings.view}
        onChange={(view) => onChange({ ...settings, view: view as MatrixGridView })}
        options={VIEW_OPTIONS.map((option) => ({
          value: option.value,
          icon: option.icon,
          ariaLabel: option.label,
        }))}
      />
      <Button
        type="button"
        variant="accent"
        size="sm"
        compact
        icon="create_new_folder"
        onClick={onNewFolder}
      >
        Nouveau dossier
      </Button>
    </div>
  );
}
